import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../config/axiosConfig";
import { logout as leadLogout } from "../slices/leadsSlice";
import { resetCurrentPageTitle } from "./pageSlice";
import { isAccountLockout, normalizeApiError, parseLockoutFromPayload } from "../../utils/lockoutUtils";

const setGroupAccessInStorage = (payload = {}) => {
  const resolvedGroupId = payload.groupid ?? payload.groupId;
  const resolvedGroupIds = payload.groupids ?? payload.groupIds;

  if (resolvedGroupId !== undefined && resolvedGroupId !== null) {
    localStorage.setItem("groupid", String(resolvedGroupId));
  } else {
    localStorage.removeItem("groupid");
  }

  if (resolvedGroupIds !== undefined && resolvedGroupIds !== null) {
    localStorage.setItem("groupids", JSON.stringify(resolvedGroupIds));
  } else {
    localStorage.removeItem("groupids");
  }
};

const persistLoginSession = (data, loginSuccessCallback) => {
  localStorage.setItem("token", data.token);
  localStorage.setItem("name", data.name);
  localStorage.setItem("lastName", data.lastName);
  localStorage.setItem("auth", data.auth);
  localStorage.setItem("userRole", data.auth);
  localStorage.setItem("Roles", data.customRoles);
  localStorage.setItem("lastName", data.lastName);
  localStorage.setItem("email", data.email);
  setGroupAccessInStorage(data);
  localStorage.setItem(
    "countrycode",
    data.countrycode || data.user?.countrycode || ""
  );
  localStorage.setItem(
    "region",
    data.region || data.user?.region || ""
  );
  localStorage.setItem("SuperUser", data.auth === "L0" ? "1" : "0");
  loginSuccessCallback?.(data.auth);
};

const clearLocalSession = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("url");
  localStorage.removeItem("groupAssigned");
  localStorage.removeItem("name");
  localStorage.removeItem("email");
  localStorage.removeItem("userRole");
  localStorage.removeItem("auth");
  localStorage.removeItem("Roles");
  localStorage.removeItem("lastName");
  localStorage.removeItem("SuperUser");
  localStorage.removeItem("countrycode");
  localStorage.removeItem("region");
  localStorage.removeItem("groupid");
  localStorage.removeItem("groupids");
};

export const tryLogin = createAsyncThunk(
  "session/tryLogin",
  async ({ email, password, loginSuccessCallback,loginFailureCallback }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/user/login", {
        email,
        password,
      });
      const data = response?.data;
      const needsVerification =
        data?.requiresVerification || (data?.verificationId && !data?.token);

      if (needsVerification) {
        return { ...data, loginEmail: email };
      }
      if (response?.data.token) {
        persistLoginSession(response.data, loginSuccessCallback);
      }
      return response.data; // Return user session data
    } catch (error) {
      const payload = normalizeApiError(error, "Login failed");
      if (!isAccountLockout(payload)) {
        loginFailureCallback?.();
      }
      return rejectWithValue(payload);
    }
  }
);

export const verifyLoginOtp = createAsyncThunk(
  "session/verifyLoginOtp",
  async (
    { verificationId, otp, email, loginSuccessCallback, loginFailureCallback },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post("/user/verify-login-otp", {
        verificationId,
        otp,
        email,
      });
      if (response?.data?.token) {
        persistLoginSession(response.data, loginSuccessCallback);
      }
      return response.data;
    } catch (error) {
      const payload = normalizeApiError(error, "Verification failed");
      if (!isAccountLockout(payload)) {
        loginFailureCallback?.();
      }
      return rejectWithValue({ ...payload, lockoutEmail: email });
    }
  }
);

export const resendLoginOtp = createAsyncThunk(
  "session/resendLoginOtp",
  async ({ verificationId, email }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/user/resend-login-otp", {
        verificationId,
        email,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        normalizeApiError(error, "Failed to resend code")
      );
    }
  }
);

export const trySuperLogin = createAsyncThunk(
  "session/trySuperLogin",
  async ({ Level, loginSuccessCallback }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/employees/switchuser", { Level });
      const { token: Switchtoken } = response.data;
      if (Switchtoken && response.data) {
        localStorage.setItem("token", Switchtoken);
        localStorage.setItem("name", response.data.name);
        localStorage.setItem("lastName", response.data.lastName);
        localStorage.setItem("auth", response.data.auth);
        localStorage.setItem("Roles", response.data.customRoles);
        localStorage.setItem('email', response.data.email)
        localStorage.setItem(
          "countrycode",
          response.data.countrycode || response.data.user?.countrycode || ""
        );
        localStorage.setItem(
          "region",
          response.data.region || response.data.user?.region || ""
        );
        localStorage.setItem("SuperUser", "1");
        localStorage.setItem("userRole", response.data.auth);
        setGroupAccessInStorage(response.data);
        loginSuccessCallback(response.data.auth);
      }
      return response.data; // Return user session data
    } catch (error) {
      return rejectWithValue(error.response?.data || "Login failed");
    }
  }
);


export const checkSession = () => (dispatch) => {
  const token = localStorage.getItem("token");
  const userRole = localStorage.getItem("userRole");
  const email = localStorage.getItem("email");
  const name = localStorage.getItem("name");
  const lastName = localStorage.getItem("lastName");
  const auth = localStorage.getItem("auth");
  const customRoles = localStorage.getItem("Roles");
  const countrycode = localStorage.getItem("countrycode");
  const region = localStorage.getItem("region");
  const groupid = localStorage.getItem("groupid");
  const groupids = localStorage.getItem("groupids");

  if (token && userRole && email) {    
    dispatch({
      type: "session/restoreSession",
      payload: {
        token,
        auth,
        userRole,
        email,
        name,
        lastName,
        customRoles,
        countrycode,
        region,
        groupid,
        groupids,
      },
    });
  } else {
    dispatch(sessionSlice.actions.logout());
  }
};


export const logoutSession = (e) => (dispatch) => {
  if (e) e.preventDefault(); // Prevent default if event exists
  return axiosInstance.post("/existing/logout").then((response) => {
    const message =
      typeof response?.data === "string" ? response.data : response?.data?.message;

    if (response?.status === 200 && message === "Logged out successfully") {
      clearLocalSession();
      dispatch(sessionSlice.actions.logout());
      dispatch(leadLogout());
      dispatch(resetCurrentPageTitle(""));
      return response.data;
    }

    return Promise.reject(response?.data || "Logout failed");
  });
};

const sessionSlice = createSlice({
  name: "session",
  initialState: { 
    session: null, 
    loading: false, 
    error: null,
    loginAttempts: 0, // ✅ Add this
    pendingVerification: null,
    otpLockout: null,
 },
  reducers: {
    logout: (state) => {
      state.session = null;
      state.loading = false;
      state.error = null;
      state.pendingVerification = null;
      state.loginAttempts = 0;
      state.otpLockout = null;
    },
    clearPendingVerification: (state) => {
      state.pendingVerification = null;
    },
    clearOtpLockout: (state) => {
      state.otpLockout = null;
    },
    restoreSession: (state, action) => {
      state.session = action.payload;
      state.loading = false;
      state.error = null;
      state.pendingVerification = null;
      state.loginAttempts = 0;
    },
        incrementLoginAttempts(state) {
      state.loginAttempts += 1;
    },
    resetLoginAttempts(state) {
      state.loginAttempts = 0;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(tryLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(tryLogin.fulfilled, (state, action) => {
        state.loading = false;
        const needsVerification =
          action.payload?.requiresVerification ||
          (action.payload?.verificationId && !action.payload?.token);

        if (needsVerification) {
          state.pendingVerification = {
            verificationId: action.payload.verificationId || "",
            maskedEmail: action.payload.email || action.payload.loginEmail || "",
            email: action.payload.loginEmail || action.payload.email || "",
            message: action.payload.message || "",
          };
          return;
        }
        state.pendingVerification = null;
        state.session = action.payload;
        if (action.payload?.token) {
          localStorage.setItem("token", action.payload.token);
          localStorage.setItem("userRole", action.payload.auth);
          localStorage.setItem("email", action.payload.email);
          localStorage.setItem("Roles", action.payload.customRoles);
          localStorage.setItem("name", action.payload.name);
          localStorage.setItem("lastName", action.payload.lastName);
          setGroupAccessInStorage(action.payload);
        }
      })
      .addCase(tryLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(verifyLoginOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyLoginOtp.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.token) {
          state.pendingVerification = null;
          state.otpLockout = null;
          state.session = action.payload;
          localStorage.setItem("token", action.payload.token);
          localStorage.setItem("userRole", action.payload.auth);
          localStorage.setItem("email", action.payload.email);
          localStorage.setItem("Roles", action.payload.customRoles);
          localStorage.setItem("name", action.payload.name);
          localStorage.setItem("lastName", action.payload.lastName);
          setGroupAccessInStorage(action.payload);
        }
      })
      .addCase(verifyLoginOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        const lockoutData = parseLockoutFromPayload(action.payload);
        if (lockoutData) {
          state.otpLockout = {
            ...lockoutData,
            email:
              action.payload?.lockoutEmail ||
              state.pendingVerification?.email ||
              "",
          };
        }
      })

    // trySuperLogin
    builder
      .addCase(trySuperLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(trySuperLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.session = action.payload;
      })
      .addCase(trySuperLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
  },

});
export const {
  incrementLoginAttempts,
  resetLoginAttempts,
  clearPendingVerification,
  clearOtpLockout,
} = sessionSlice.actions;
export default sessionSlice.reducer;